import { cloneData } from './clone.ts';
import { newId } from './ids.ts';
import { secondsToFrame } from './time.ts';
import {
  SCHEMA_VERSION,
  type AudioClip,
  type Clip,
  type ImageClip,
  type MediaAsset,
  type Project,
  type ProjectSettings,
  type Sequence,
  type SequenceClip,
  type SolidClip,
  type TextClip,
  type TextStyle,
  type Track,
  type TrackKind,
  type VideoClip,
} from './schema.ts';

export const DEFAULT_SETTINGS: ProjectSettings = {
  width: 1920,
  height: 1080,
  fps: { num: 30, den: 1 },
  sampleRate: 48000,
  backgroundColor: '#000000',
  blending: 'linear',
};

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'Inter, Helvetica, Arial, sans-serif',
  fontSize: 96,
  fontWeight: 700,
  italic: false,
  color: '#ffffff',
  align: 'center',
  lineHeight: 1.2,
  strokeColor: '#000000',
  strokeWidth: 0,
  shadowColor: '#000000aa',
  shadowBlur: 12,
  padding: 24,
};

/** Default duration (seconds) for clips without intrinsic length (images, text, solids). */
export const DEFAULT_STILL_SECONDS = 5;

export function createTrack(kind: TrackKind, name: string): Track {
  return {
    id: newId('trk'),
    kind,
    name,
    clips: [],
    transitions: [],
    muted: false,
    solo: false,
    locked: false,
    hidden: false,
    volume: 1,
  };
}

export function createSequence(name: string): Sequence {
  return {
    id: newId('seq'),
    name,
    videoTracks: [createTrack('video', 'V1'), createTrack('video', 'V2')],
    audioTracks: [createTrack('audio', 'A1'), createTrack('audio', 'A2')],
    markers: [],
  };
}

export function createProject(name: string, settings: Partial<ProjectSettings> = {}): Project {
  const root = createSequence('Main');
  const now = new Date().toISOString();
  return {
    schemaVersion: SCHEMA_VERSION,
    id: newId('prj'),
    name,
    createdAt: now,
    updatedAt: now,
    settings: { ...DEFAULT_SETTINGS, ...settings },
    rootSequenceId: root.id,
    sequences: { [root.id]: root },
    media: {},
    mediaOrder: [],
    mediaFolders: {},
  };
}

const visualDefaults = { fit: 'contain', blendMode: 'normal' } as const;

/**
 * Builds a clip for a media asset, sized to the media's natural duration.
 * The caller decides the start frame and the track.
 */
export function createClipFromMedia(
  asset: MediaAsset,
  settings: ProjectSettings,
  start: number,
): VideoClip | ImageClip | AudioClip {
  const base = { id: newId('clp'), name: asset.name, start, enabled: true, params: {} };
  const mediaFrames = asset.duration
    ? Math.max(1, secondsToFrame(asset.duration, settings.fps))
    : secondsToFrame(DEFAULT_STILL_SECONDS, settings.fps);
  switch (asset.kind) {
    case 'video':
      return {
        ...base,
        ...visualDefaults,
        type: 'video',
        mediaId: asset.id,
        duration: mediaFrames,
        sourceIn: 0,
        speed: 1,
        muted: false,
      };
    case 'image':
      return { ...base, ...visualDefaults, type: 'image', mediaId: asset.id, duration: mediaFrames };
    case 'audio':
      return {
        ...base,
        type: 'audio',
        mediaId: asset.id,
        duration: mediaFrames,
        sourceIn: 0,
        speed: 1,
        muted: false,
      };
  }
}

export function createTextClip(start: number, duration: number, text = 'Title'): TextClip {
  return {
    id: newId('clp'),
    name: text,
    type: 'text',
    start,
    duration,
    enabled: true,
    params: {},
    ...visualDefaults,
    // Text is rasterised at its natural pixel size; fitting it to the frame would scale the font.
    fit: 'none',
    text,
    style: { ...DEFAULT_TEXT_STYLE },
  };
}

export function createSolidClip(start: number, duration: number, color = '#202020'): SolidClip {
  return {
    id: newId('clp'),
    name: 'Solid',
    type: 'solid',
    start,
    duration,
    enabled: true,
    params: {},
    ...visualDefaults,
    fit: 'stretch',
    color,
  };
}

export function createSequenceClip(sequence: Sequence, start: number, duration: number): SequenceClip {
  return {
    id: newId('clp'),
    name: sequence.name,
    type: 'sequence',
    sequenceId: sequence.id,
    start,
    duration,
    enabled: true,
    params: {},
    ...visualDefaults,
    sourceIn: 0,
    muted: false,
  };
}

/** Deep-clones a clip with fresh ids (clip and keyframes). */
export function cloneClip<T extends Clip>(clip: T): T {
  const copy = cloneData(clip);
  copy.id = newId('clp');
  for (const param of Object.values(copy.params)) {
    for (const kf of param?.keyframes ?? []) kf.id = newId('kf');
  }
  return copy;
}
